// Per-loan assignments (credit analyst, underwriter, doc & funding): those roles see only loans they
// are assigned to. Managers with `manage_assignments` add/remove; others see the list read-only.
// Every grant and revoke is written to the access log by the server.
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, ApiError } from "../lib/api";
import { hasCap, roleLabel } from "../lib/roles";
import { securityKeys, useLoanAssignments, useSecurityAccess, type LoanAssignment } from "../lib/security";
import type { Me } from "../lib/types";
import { seatCandidates } from "../lib/acting";
import { Button, Spinner } from "./ui";

/** Active users whose role is assignable and who are not yet assigned to this loan. */
export function assignableCandidates(
  users: { id: string; display_name: string; role: string; status: string }[],
  roles: { role: string; assignable: boolean }[],
  assigned: Pick<LoanAssignment, "user_id">[],
) {
  const assignable = new Set(roles.filter((r) => r.assignable).map((r) => r.role));
  const taken = new Set(assigned.map((a) => a.user_id));
  return users
    .filter((u) => u.status === "active" && assignable.has(u.role) && !taken.has(u.id))
    .sort((a, b) => a.display_name.localeCompare(b.display_name));
}

export function AssignedStaff({ loanId, me }: { loanId: string; me: Me }) {
  const canManage = hasCap(me, "manage_assignments");
  const q = useLoanAssignments(loanId);
  const access = useSecurityAccess(canManage);
  const qc = useQueryClient();
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  // Not allowed to see assignments for this loan: show nothing rather than an error.
  if (q.error instanceof ApiError && (q.error.status === 403 || q.error.status === 404)) return null;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: securityKeys.assignments(loanId) });
    void qc.invalidateQueries({ queryKey: securityKeys.access });
  };
  const add = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const [userId, role] = pick.split("|");
      await api(`/api/loans/${loanId}/assignments/${userId}`, { method: "PUT", body: { assignment_role: role } });
      setPick("");
      toast.success("Assigned");
    } catch (e) {
      toast.error("Couldn't assign", { description: (e as Error).message });
    } finally {
      setBusy(false);
      refresh();
    }
  };
  const remove = async (a: LoanAssignment) => {
    setBusy(true);
    try {
      await api(`/api/loans/${loanId}/assignments/${a.user_id}`, { method: "DELETE" });
      toast.success(`${a.display_name} removed`);
    } catch (e) {
      toast.error("Couldn't remove", { description: (e as Error).message });
    } finally {
      setBusy(false);
      refresh();
    }
  };

  const list = q.data ?? [];
  const candidates = access.data ? seatCandidates(access.data.users, access.data.roles, access.data.acting_seats ?? [], list) : [];
  return (
    <section className="mt-5">
      <h3 className="eyebrow mb-2 border-b border-subtle pb-1 text-ink-3">Assigned staff</h3>
      {q.isLoading ? (
        <Spinner />
      ) : q.isError ? (
        <div className="text-[13px] text-ink-2">Couldn't load assignments.</div>
      ) : (
        <div className="space-y-2 text-[13px]">
          {list.length ? (
            <ul className="space-y-1">
              {list.map((a) => (
                <li key={a.user_id} className="flex items-center gap-2">
                  <span className="font-medium text-ink">{a.display_name}</span>
                  <span className="text-ink-3">{roleLabel(a.assignment_role)}</span>
                  {canManage ? (
                    <button
                      className="focus-ring ml-auto rounded p-1 text-ink-3 hover:bg-hover hover:text-ink"
                      aria-label={`Remove ${a.display_name}`}
                      disabled={busy}
                      onClick={() => void remove(a)}
                    >
                      <X size={14} />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-ink-2">No one is assigned. Credit analysts, underwriters, doc & funding and account managers (including people acting in those seats) see only loans they are assigned to.</div>
          )}
          {canManage ? (
            <div className="flex items-center gap-2">
              <select
                aria-label="Person to assign"
                value={pick}
                onChange={(e) => setPick(e.target.value)}
                className="focus-ring h-8 min-w-0 flex-1 rounded-md border border-input bg-surface px-2 text-[13px] text-ink"
              >
                <option value="">{candidates.length ? "Assign someone…" : "No one else to assign"}</option>
                {candidates.map((u) => (
                  <option key={`${u.user_id}|${u.role}`} value={`${u.user_id}|${u.role}`}>
                    {u.display_name} · {roleLabel(u.role)}
                    {u.acting ? " (acting seat)" : ""}
                  </option>
                ))}
              </select>
              <Button size="sm" disabled={!pick || busy} onClick={() => void add()}>
                Assign
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
